import { useForm } from "react-hook-form";
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
// import useGoogleAuth from "../../hooks/useGoogleAuth"; // Commented out - Google auth not ready
import { signIn, validateSession } from "../../api/authApi";

import InputLabel from "../Shared/InputLabel";
import Input from "../Shared/Input";
import Button from "./Button";
import CTA from "./CTA";
// import SSOButtonWrapper from "./SSOButtonWrapper"; // Commented out - only used for Google auth
// import GoogleSignInButton from "./GoogleSignInButton"; // Commented out - Google auth not ready
// import SeperatorWithText from "./SeperatorWithText"; // Commented out - only used for Google auth
import ForgotPasswordLink from "./ForgotPasswordLink";
import FormWrapper from "./FormWrapper";
import Checkbox from "../Shared/Checkbox";
import AlertRed from "../Shared/AlertRed";

const SignIn = () => {
  const [alert, setAlert] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from;
  const redirectTo = from
    ? `${from.pathname}${from.search || ""}${from.hash || ""}`
    : "/dashboard";
  // const { googleLoginHandler } = useGoogleAuth(); // Commented out - Google auth not ready
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm();

  const onSubmit = async (data) => {
    try {
      await signIn(data);
      navigate(redirectTo, { replace: true });
    } catch (error) {
      const statusCode = error?.response?.status;
      switch (statusCode) {
        case 401:
        case 404:
          setAlert(
            <AlertRed
              title="Sign-In Error"
              message="It looks like the email or password you entered is incorrect. Please try again."
            />
          );
          break;
        default:
          setAlert(
            <AlertRed
              title="Sign-In Error"
              message="We're having trouble signing you in right now. Please verify your details and try again. If you're still having issues, please try again later."
            />
          );
          break;
      }
    }
  };

  // Check if user is already logged in and redirect to dashboard
  useEffect(() => {
    const checkExistingSession = async () => {
      try {
        await validateSession();
        navigate(redirectTo, { replace: true });
      } catch (error) {
        // No valid session, stay on signin page
      }
    };
    checkExistingSession();
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <CTA
        question="Don't have an account?"
        linkText="Create an account"
        page="signup"
      />
      <FormWrapper>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <InputLabel htmlFor="email">Email address</InputLabel>
            <Input
              id="email"
              name="email"
              errormessage={errors?.email?.message}
              {...register("email", {
                required: "Email is required",
                pattern: {
                  value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
                  message: "Invalid email address",
                },
              })}
            />
          </div>
          <div>
            <InputLabel htmlFor="password">Password</InputLabel>
            <Input
              id="password"
              type="password"
              errormessage={errors?.password?.message}
              {...register("password", {
                required: "Password is required"
              })}
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Checkbox
                id="rememberMe"
                type="checkbox"
                {...register("rememberMe")}
                label="Remember Me"
              />
              <p className="ml-7 text-xs text-ink-500">
                Keep me signed in for 14 days
              </p>
            </div>
            <ForgotPasswordLink />
          </div>
          {alert ? alert : null}
          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-accent-500 hover:bg-accent-400 focus-visible:outline-accent-500"
          >
            {isSubmitting ? "Signing in..." : "Sign in"}
          </Button>
        </form>
        {/* Commented out Google sign-in - not ready for production
        <div>
          <SeperatorWithText text="Or continue with" />
          <SSOButtonWrapper>
            <GoogleSignInButton googleLoginHandler={googleLoginHandler} />
          </SSOButtonWrapper>
        </div>
        */}
      </FormWrapper>
    </div>
  );
};

export default SignIn;
